import type { PlatformId, ReliabilityBreakdown } from './types';

// Score de fiabilité déterministe (0-100), décomposé en 4 axes :
//   régulation/juridiction (0-40) + ancienneté (0-25) + avis (0-25) + transparence (0-10).
// Aucune dépendance au temps réel : l'ancienneté est calculée sur une année de
// référence fixe (alignée sur `lastVerified` du dataset) pour rester testable.

/** Année de référence pour l'ancienneté (dataset vérifié le 2026-07-24). */
const REFERENCE_YEAR = 2026;

/** Sous-ensemble de champs nécessaire au calcul, partagé par Propfirm et Broker. */
export interface ReliabilityInput {
  regulation: readonly string[];
  foundedYear: number | null;
  headquarters: string | null;
  trustpilotScore: number | null;
  trustpilotReviews: number | null;
  platforms: readonly PlatformId[];
}

export interface ReliabilityResult {
  /** Somme des composantes, arrondie, bornée à [0, 100]. */
  score: number;
  breakdown: ReliabilityBreakdown;
}

// --- Régulation (brokers) ---------------------------------------------------
// Pondération par qualité du régulateur. Un broker multi-régulé plafonne à 40.
const REGULATOR_TIER1 = 14; // FCA, ASIC, CFTC, NFA, CySEC, BaFin, FINMA, MAS, JFSA
const REGULATOR_TIER2 = 7; //  CIRO, FMA, DFSA, FSCA, SCB, CIMA, MFSA, JSC, KNF
const REGULATOR_TIER3 = 4; //  reste (FSA/SC, FSC, CMA, VFSC, CBCS, BMA…)

const TIER1_REGULATORS = new Set([
  'FCA',
  'ASIC',
  'CFTC',
  'NFA',
  'CYSEC',
  'BAFIN',
  'FINMA',
  'MAS',
  'JFSA',
]);
const TIER2_REGULATORS = new Set([
  'CIRO',
  'FMA',
  'DFSA',
  'FSCA',
  'SCB',
  'CIMA',
  'MFSA',
  'JSC',
  'KNF',
]);

function regulatorWeight(acronym: string): number {
  if (TIER1_REGULATORS.has(acronym)) return REGULATOR_TIER1;
  if (TIER2_REGULATORS.has(acronym)) return REGULATOR_TIER2;
  return REGULATOR_TIER3;
}

export type RegulatorTier = 1 | 2 | 3;

/** Palier de qualité d'un régulateur (1 = plus strict), pour l'affichage en badge. */
export function regulatorTier(entry: string): RegulatorTier {
  const acronym = entry.split('(')[0].trim().split('/')[0].trim().toUpperCase();
  if (TIER1_REGULATORS.has(acronym)) return 1;
  if (TIER2_REGULATORS.has(acronym)) return 2;
  return 3;
}

function regulationFromRegulators(regulation: readonly string[]): number {
  let sum = 0;
  for (const entry of regulation) {
    // "CFTC/NFA (US)" → ["CFTC", "NFA"] ; "ASIC (AU)" → ["ASIC"].
    const acronyms = entry.split('(')[0].trim().split('/');
    for (const raw of acronyms) {
      const acronym = raw.trim().toUpperCase();
      if (acronym) sum += regulatorWeight(acronym);
    }
  }
  return Math.min(40, sum);
}

// --- Juridiction (propfirms, non régulées par nature) -----------------------
// Barème distinct plafonné à 26 (< 40 broker) : sans régulation effective, la
// protection structurelle reste inférieure même dans une juridiction sérieuse.
const JURISDICTION_TIER1 = 26; // US / UK / UE (siège dans une place réglementée forte)
const JURISDICTION_TIER2 = 18; // EAU, Australie, Israël
const JURISDICTION_TIER3 = 9; //  offshore (SVG, Seychelles, Vanuatu, BVI, Belize)
const JURISDICTION_UNKNOWN = 5; // siège inconnu → baseline minimal

const JURISDICTION_KEYWORDS: ReadonlyArray<readonly [number, readonly string[]]> = [
  [JURISDICTION_TIER3, ['saint-vincent', 'grenadines', 'seychelles', 'vanuatu', 'belize', 'bvi']],
  [JURISDICTION_TIER2, ['eau', 'émirats', 'emirats', 'dubaï', 'dubai', 'australie', 'israël', 'israel']],
  [JURISDICTION_TIER1, ['usa', 'états-unis', 'etats-unis', 'royaume-uni', 'uk', 'tchéquie', 'espagne']],
];

function regulationFromJurisdiction(headquarters: string | null): number {
  if (!headquarters) return JURISDICTION_UNKNOWN;
  const hq = headquarters.toLowerCase();
  for (const [score, keywords] of JURISDICTION_KEYWORDS) {
    if (keywords.some((k) => hq.includes(k))) return score;
  }
  return JURISDICTION_UNKNOWN;
}

// --- Ancienneté -------------------------------------------------------------
function seniorityScore(foundedYear: number | null): number {
  if (foundedYear === null) return 0;
  const years = Math.max(0, REFERENCE_YEAR - foundedYear);
  return Math.min(25, years * 5); // plein (25) dès 5 ans
}

// --- Avis Trustpilot pondérés par le volume ---------------------------------
const VOLUME_REFERENCE = 20000; // volume donnant une pleine confiance
const VOLUME_FLOOR = 0.5; // poids minimal accordé à la qualité même à faible volume

function reviewsScore(score: number | null, reviews: number | null): number {
  if (score === null) return 0;
  const quality = Math.max(0, Math.min(1, score / 5));
  const count = reviews ?? 0;
  const volumeConfidence = Math.min(1, Math.log(count + 1) / Math.log(VOLUME_REFERENCE + 1));
  const weight = VOLUME_FLOOR + (1 - VOLUME_FLOOR) * volumeConfidence;
  return 25 * quality * weight;
}

// --- Transparence (complétude des données) ----------------------------------
function transparencyScore(entity: ReliabilityInput): number {
  const checks = [
    entity.foundedYear !== null,
    entity.headquarters !== null,
    entity.trustpilotScore !== null,
    entity.trustpilotReviews !== null,
    entity.platforms.length > 0,
  ];
  const present = checks.filter(Boolean).length;
  return (present / checks.length) * 10;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

export function computeReliability(entity: ReliabilityInput): ReliabilityResult {
  const regulation = round1(
    entity.regulation.length > 0
      ? regulationFromRegulators(entity.regulation)
      : regulationFromJurisdiction(entity.headquarters),
  );
  const seniority = round1(seniorityScore(entity.foundedYear));
  const reviews = round1(reviewsScore(entity.trustpilotScore, entity.trustpilotReviews));
  const transparency = round1(transparencyScore(entity));
  const score = Math.max(0, Math.min(100, Math.round(regulation + seniority + reviews + transparency)));
  return { score, breakdown: { regulation, seniority, reviews, transparency } };
}
